import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Box, CssBaseline } from "@mui/material";
import { GeoPoint, Timestamp, doc, getDoc, updateDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import moment from "moment";

import DetailsPageHeader from "../../../../common/DetailsPageHeader";
import { mainLayoutStyles, showNotification } from "../../../../constants";
import Sidebar from "../../../../common/Sidebar";
import ImagePreviewModal from "../../../../common/ImagePreviewModal";
import AudioModal from "../../../../common/AudioModal";
import ConfirmationModal from "../../../../common/ConfirmationModal";
import QuickTaleSavedModal from "../../../../common/QuickTaleSavedModal";
import QuickTaleWarningModal from "../../../../common/QuickTaleWarningModal";
import CreateQTDataFieldsSection from "../../components/CreateQTDataFieldsSection/CreateQTDataFieldsSection";
import CreateQTDetailsInteractionsFieldsSection from "../../components/CreateQTDetailsInteractionsFieldsSection/CreateQTDetailsInteractionsFieldsSection";
import {
  createQuickTale,
  uploadQuickTaleAudioToStorage,
  uploadQuickTaleImageToStorage,
  uploadProcessedAudioToStorage,
  getSingleUser,
  getSingleQuickTale,
  getSingleAdmin,
} from "../../hooks/QuickTalesHooks";
import {
  createCustomNarrator,
  narratorNameExists,
  getCustomNarratorsByUser,
} from "../../hooks/CustomNarratorsHooks";
import { getSingleOrg } from "../../../OrgProf/hook/Organizationhook";
import {
  getLocationFromOrganization,
  getLocationFromBrowser,
  getLocationFromIP,
  getDefaultLocation,
  hasValidOrgLocation,
} from "../../../../utils/locationUtils";
import { upsertQuickTaleInAlbumsAndQTs } from "../../components/QTDetailsDataFieldsSection/QTAreasField";
import { db } from "../../../../firebase";
import { useQuickTalesContext } from "../../../../providers/OrganizationAdmin/QuickTalesProvider";
import { Toast } from "../../../../common/alert";
import { useAuth } from "../../../../hooks";

const CreateQuickTaleContainer = () => {
  const { currentUser, organizationId } = useAuth();
  const { setLoading, loading } = useQuickTalesContext();
  const navigate = useNavigate();
  const [quicktaleDetails, setQuicktaleDetails] = useState({});
  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [audioModal, setAudioModal] = useState(false);
  const [processedAudioModal, setProcessedAudioModal] = useState(false);
  const [uploadImage, setUploadImage] = useState(null);
  const [uploadAudioFile, setUploadAudioFile] = useState(null);
  const [uploadProcessedAudioFile, setUploadProcessedAudioFile] = useState(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [cancelConfirmationModalOpen, setCancelConfirmationModalOpen] = useState(false);
  const [locationPermissionModalOpen, setLocationPermissionModalOpen] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [quicktaleSavedModalOpen, setQuicktaleSavedModalOpen] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [warningModalType, setWarningModalType] = useState('missing');
  const [missingFields, setMissingFields] = useState([]);
  const [isDraftEnabled, setisDraftEnabled] = useState(true);

  // Initialize with default values and location
  useEffect(() => {
    const initializeQuickTale = async () => {
      setLoading(true);
      const currentTimestamp = Timestamp.now();
      
      // Get current user data for createdBy and narrator
      let createdByData = null;
      let narratorData = null;
      let actualUserId = currentUser?.id || "";
      
      if (currentUser?.id) {
        try {
          // First, get the admin record to find the userId
          console.log("Fetching admin record for currentUser id:", currentUser.id);
          const adminDoc = await getSingleAdmin(currentUser.id);
          console.log("Admin doc fetched:", adminDoc?.exists());
          
          if (adminDoc && adminDoc.exists()) {
            const adminData = adminDoc.data();
            console.log("Admin data:", adminData);
            
            // Get the userId from the admin record
            actualUserId = adminData.userId || currentUser.id;
            console.log("Actual userId from admin record:", actualUserId);
            
            // Now fetch the actual user data using the userId
            const userDoc = await getSingleUser(actualUserId);
            console.log("User doc fetched:", userDoc?.exists());
            
            if (userDoc && userDoc.exists()) {
              const userData = userDoc.data();
              console.log("User data:", userData);
              
              // Ensure the data structure matches what the component expects
              createdByData = {
                id: actualUserId,
                firstName: userData.firstName || "",
                lastName: userData.lastName || "",
                userName: userData.userName || "",
                ...userData
              };
              narratorData = {
                id: actualUserId,
                firstName: userData.firstName || "",
                lastName: userData.lastName || "",
                userName: userData.userName || "",
                ...userData
              };
              
              console.log("Created By Data:", createdByData);
            } else {
              console.warn("User document not found, using admin data as fallback");
              // Fallback to admin data if user document doesn't exist
              createdByData = {
                id: actualUserId,
                firstName: adminData.firstName || "",
                lastName: adminData.lastName || "",
                userName: adminData.userName || "",
              };
              narratorData = {
                id: actualUserId,
                firstName: adminData.firstName || "",
                lastName: adminData.lastName || "",
                userName: adminData.userName || "",
              };
            }
          } else {
            console.warn("Admin document not found, using currentUser data as fallback");
            // Fallback to currentUser data if admin document doesn't exist
            createdByData = {
              id: currentUser.id,
              firstName: currentUser.firstName || "",
              lastName: currentUser.lastName || "",
              userName: currentUser.userName || "",
            };
            narratorData = {
              id: currentUser.id,
              firstName: currentUser.firstName || "",
              lastName: currentUser.lastName || "",
              userName: currentUser.userName || "",
            };
          }
        } catch (error) {
          console.error("Error fetching admin/user data:", error);
          // Fallback to currentUser data on error
          createdByData = {
            id: currentUser.id,
            firstName: currentUser.firstName || "",
            lastName: currentUser.lastName || "",
            userName: currentUser.userName || "",
          };
          narratorData = {
            id: currentUser.id,
            firstName: currentUser.firstName || "",
            lastName: currentUser.lastName || "",
            userName: currentUser.userName || "",
          };
        }
      }

      // Default createdBy to the organization id (not the current user).
      // The organization document is fetched below, at which point
      // createdByData is populated with the org's display data.
      const defaultOrgId = organizationId || currentUser?.organization || "";

      // Initialize base QuickTale details
      const baseDetails = {
        title: "",
        description: "",
        status: "public",
        approvalStatus: {
          status: "approved",
          updatedAt: currentTimestamp,
          updatedBy: currentUser?.id || "",
        },
        createdBy: defaultOrgId,
        createdByData: null,
        narrator: actualUserId,
        narratorData: narratorData,
        organization: defaultOrgId,
        isDraft: true,

        createdAt: currentTimestamp,
        tags: [],
        location: "",
        locationDisplay: "",
        locationCountry: "",
        coordinates: new GeoPoint(0, 0),
        g: {
          geohash: "",
          geopoint: new GeoPoint(0, 0),
        },
        audioLength: 0,
        imageUrls: [],
        audioUrl: "",
        processedAudioUrl: "",
        originalAudioName: "",
        processedAudioName: "",
        originalPhotoName: "",
      };

      // Try to get location from organization.
      // Also use the org document to populate createdByData so the
      // "Created By" field displays the organization name by default.
      try {
        const orgId = organizationId || currentUser?.organization;
        if (orgId) {
          const orgDoc = await getSingleOrg(orgId);
          if (orgDoc && orgDoc.exists()) {
            const orgData = orgDoc.data();
            console.log("Organization data fetched:", orgData);

            // Populate createdByData from the organization (createdBy is the org id).
            baseDetails.createdByData = {
              ...orgData,
              id: orgId,
              _sourceType: "organization",
            };

            // Check if organization has valid location
            if (hasValidOrgLocation(orgData)) {
              console.log("Organization has valid location, extracting...");
              const locationData = await getLocationFromOrganization(orgData);
              
              if (locationData) {
                console.log("Location extracted from organization:", locationData);
                setQuicktaleDetails({
                  ...baseDetails,
                  ...locationData
                });
                setLoading(false);
                return;
              }
            }
          }
        }

        
        // No valid organization location found, check if we already asked this session
        console.log("No valid organization location, checking session storage");
        const locationPermissionAsked = sessionStorage.getItem('locationPermissionAsked');
        const useCurrentLocation = sessionStorage.getItem('useCurrentLocation');
        
        if (locationPermissionAsked === 'true' && useCurrentLocation) {
          // Already asked this session, use stored preference
          console.log("Using stored location preference:", useCurrentLocation);
          setQuicktaleDetails(baseDetails);
          setLoading(false);
          
          if (useCurrentLocation === 'true') {
            // User previously chose to use current location
            handleUseCurrentLocation();
          } else {
            // User previously declined current location
            handleDeclineCurrentLocation();
          }
        } else {
          // First time this session, show permission modal
          console.log("First time asking for location this session");
          setQuicktaleDetails(baseDetails);
          setLoading(false);
          setLocationPermissionModalOpen(true);
        }
        
      } catch (error) {
        console.error("Error initializing location:", error);
        // On error, check session storage before showing modal
        const locationPermissionAsked = sessionStorage.getItem('locationPermissionAsked');
        const useCurrentLocation = sessionStorage.getItem('useCurrentLocation');
        
        setQuicktaleDetails(baseDetails);
        setLoading(false);
        
        if (locationPermissionAsked === 'true' && useCurrentLocation) {
          if (useCurrentLocation === 'true') {
            handleUseCurrentLocation();
          } else {
            handleDeclineCurrentLocation();
          }
        } else {
          setLocationPermissionModalOpen(true);
        }
      }
    };

    initializeQuickTale();
  }, [currentUser, organizationId]);

  const handleProcessOriginalAudio = () => {
    setConfirmationModalOpen(true);
  };

  const handleConfirmationModalClose = () => {
    setConfirmationModalOpen(false);
    // User chose not to process the audio
    setQuicktaleDetails((prev) => ({
      ...prev,
      userNoProcess: true,
    }));
  };

  const handleProcessAudio = async () => {
    setConfirmationModalOpen(false);
    if (!quicktaleDetails.audioUrl) {
      Toast("error", "No original audio file to process.");
      return;
    }
    setLoading(true);
    try {
      const functions = getFunctions();
      const processAudio = httpsCallable(functions, 'processAudioWithAuphonicV3');
      const result = await processAudio({
        fileUrl: quicktaleDetails.audioUrl,
        qtTitle: quicktaleDetails.title,
        quicktaleId: "temp",
        ogFileName: quicktaleDetails.originalAudioName || 'audio'
      });

      const { success, filePath, processedAudioName, error } = result.data;

      if (success) {
        const audio = new Audio(filePath);
        await new Promise((resolve) => {
          audio.onloadedmetadata = () => {
            const duration = Math.floor(audio.duration);
            setQuicktaleDetails((prev) => ({
              ...prev,
              processedAudioUrl: filePath,
              processedAudioName: processedAudioName || `processed_${quicktaleDetails.originalAudioName}`,
              audioLength: duration,
              userNoProcess: false, // User chose to process the audio
            }));
            resolve();
          };
        });
        showNotification("Audio successfully processed and updated.");
      } else {
        throw new Error(error || 'Failed to process audio.');
      }
    } catch (error) {
      Toast("error", `Error processing audio: ${error.message}`);
      console.error("Error calling processAudioWithAuphonicV2:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;

    setLoading(true);
    try {
      let dataTemp = {};

      if (type === "original") {
        const audioUrl = await uploadQuickTaleAudioToStorage(file.raw);
        dataTemp = {
          audioUrl: audioUrl,
          originalAudioName: file.raw.name,
        };
        
        const audio = new Audio(audioUrl);
        await new Promise((resolve) => {
          audio.onloadedmetadata = () => {
            const duration = Math.floor(audio.duration);
            dataTemp.audioLength = duration;
            setQuicktaleDetails((prev) => ({ 
              ...prev, 
              ...dataTemp,
              audioLength: duration 
            }));
            resolve();
          };
        });
        
        // Audio uploaded — processing will happen fire-and-forget after save via V5
        showNotification("Audio uploaded successfully.");
      } else if (type === "processed") {
        const processedAudioUrl = await uploadProcessedAudioToStorage(file.raw);
        dataTemp = {
          processedAudioUrl: processedAudioUrl,
          processedAudioName: file.raw.name,
        };
        
        const audio = new Audio(processedAudioUrl);
        await new Promise((resolve) => {
          audio.onloadedmetadata = () => {
            const duration = Math.floor(audio.duration);
            dataTemp.audioLength = duration;
            setQuicktaleDetails((prev) => ({ 
              ...prev, 
              ...dataTemp,
              audioLength: duration 
            }));
            resolve();
          };
        });
        
        showNotification(`Processed audio file uploaded successfully.`);
      }
    } catch (error) {
      Toast("error", `Error uploading file: ${error.message}`);
      console.error("Error uploading file:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImagePreviewModalOpen = () => {
    setImagePreviewModal(true);
  };
  const handleImagePreviewModalClose = () => {
    setImagePreviewModal(false);
  };

  const handleAudioModalOpen = () => {
    setAudioModal(true);
  };
  const handleAudioModalClose = () => {
    setAudioModal(false);
  };

  const handleProcessedAudioModalOpen = () => {
    setProcessedAudioModal(true);
  };
  const handleProcessedAudioModalClose = () => {
    setProcessedAudioModal(false);
  };

  const handleCancelClick = () => {
    setCancelConfirmationModalOpen(true);
  };

  const handleConfirmCancel = () => {
    setCancelConfirmationModalOpen(false);
    navigate('/quick-tales-in-organization');
  };

  const handleUseCurrentLocation = async () => {
    setLocationPermissionModalOpen(false);
    setLoadingLocation(true);
    
    // Store user's preference in sessionStorage
    sessionStorage.setItem('locationPermissionAsked', 'true');
    sessionStorage.setItem('useCurrentLocation', 'true');
    
    try {
      // Try browser geolocation first
      console.log("Requesting browser geolocation...");
      const locationData = await getLocationFromBrowser();
      console.log("Browser location obtained:", locationData);
      
      setQuicktaleDetails(prev => ({
        ...prev,
        ...locationData
      }));
    } catch (error) {
      console.error("Browser geolocation failed, trying IP location:", error);
      
      // Fall back to IP-based location
      try {
        const locationData = await getLocationFromIP();
        console.log("IP-based location obtained:", locationData);
        
        setQuicktaleDetails(prev => ({
          ...prev,
          ...locationData
        }));
      } catch (ipError) {
        console.error("IP location failed, using default location:", ipError);
        
        // Fall back to Hayes, Kansas
        try {
          const locationData = await getDefaultLocation();
          console.log("Using default location (Hayes, KS):", locationData);
          
          setQuicktaleDetails(prev => ({
            ...prev,
            ...locationData
          }));
          showNotification("Location set to default (Hayes, Kansas)");
        } catch (defaultError) {
          console.error("Even default location failed:", defaultError);
          Toast("error", "Unable to set location. Please set manually.");
        }
      }
    } finally {
      setLoadingLocation(false);
    }
  };

  const handleDeclineCurrentLocation = async () => {
    setLocationPermissionModalOpen(false);
    setLoadingLocation(true);
    
    // Store user's preference in sessionStorage
    sessionStorage.setItem('locationPermissionAsked', 'true');
    sessionStorage.setItem('useCurrentLocation', 'false');
    
    try {
      // Use IP-based location
      console.log("User declined browser location, using IP location...");
      const locationData = await getLocationFromIP();
      console.log("IP-based location obtained:", locationData);
      
      setQuicktaleDetails(prev => ({
        ...prev,
        ...locationData
      }));
    } catch (error) {
      console.error("IP location failed, using default location:", error);
      
      // Fall back to Hayes, Kansas
      try {
        const locationData = await getDefaultLocation();
        console.log("Using default location (Hayes, KS):", locationData);
        
        setQuicktaleDetails(prev => ({
          ...prev,
          ...locationData
        }));
        showNotification("Location set to default (Hayes, Kansas)");
      } catch (defaultError) {
        console.error("Even default location failed:", defaultError);
        Toast("error", "Unable to set location. Please set manually.");
      }
    } finally {
      setLoadingLocation(false);
    }
  };

  const handleWarningModalSaveAsDraft = () => {
    setWarningModalOpen(false);
    // Ensure isDraft is set to true
    setQuicktaleDetails(prev => ({ ...prev, isDraft: true }));
    // Proceed with save
    performSave();
  };

  const handleWarningModalFinishQuickTale = () => {
    setWarningModalOpen(false);
    // Set isDraft to false
    setQuicktaleDetails(prev => ({ ...prev, isDraft: false }));
    // Proceed with save
    performSave();
  };

  const handleWarningModalClose = () => {
    setWarningModalOpen(false);
  };

  const handleReturnToList = () => {
    setQuicktaleSavedModalOpen(false);
    navigate('/quick-tales-in-organization');
  };

  const handleCreateAnother = async () => {
    setQuicktaleSavedModalOpen(false);
    setLoading(true);
    
    try {
      // Reset all upload states
      setUploadImage(null);
      setUploadAudioFile(null);
      setUploadProcessedAudioFile(null);
      
      // Get current user data again
      const currentTimestamp = Timestamp.now();
      let createdByData = null;
      let narratorData = null;
      let actualUserId = currentUser?.id || "";
      
      if (currentUser?.id) {
        try {
          const adminDoc = await getSingleAdmin(currentUser.id);
          
          if (adminDoc && adminDoc.exists()) {
            const adminData = adminDoc.data();
            actualUserId = adminData.userId || currentUser.id;
            
            const userDoc = await getSingleUser(actualUserId);
            
            if (userDoc && userDoc.exists()) {
              const userData = userDoc.data();
              createdByData = {
                id: actualUserId,
                firstName: userData.firstName || "",
                lastName: userData.lastName || "",
                userName: userData.userName || "",
                ...userData
              };
              narratorData = {
                id: actualUserId,
                firstName: userData.firstName || "",
                lastName: userData.lastName || "",
                userName: userData.userName || "",
                ...userData
              };
            } else {
              createdByData = {
                id: actualUserId,
                firstName: adminData.firstName || "",
                lastName: adminData.lastName || "",
                userName: adminData.userName || "",
              };
              narratorData = {
                id: actualUserId,
                firstName: adminData.firstName || "",
                lastName: adminData.lastName || "",
                userName: adminData.userName || "",
              };
            }
          } else {
            createdByData = {
              id: currentUser.id,
              firstName: currentUser.firstName || "",
              lastName: currentUser.lastName || "",
              userName: currentUser.userName || "",
            };
            narratorData = {
              id: currentUser.id,
              firstName: currentUser.firstName || "",
              lastName: currentUser.lastName || "",
              userName: currentUser.userName || "",
            };
          }
        } catch (error) {
          console.error("Error fetching admin/user data:", error);
          createdByData = {
            id: currentUser.id,
            firstName: currentUser.firstName || "",
            lastName: currentUser.lastName || "",
            userName: currentUser.userName || "",
          };
          narratorData = {
            id: currentUser.id,
            firstName: currentUser.firstName || "",
            lastName: currentUser.lastName || "",
            userName: currentUser.userName || "",
          };
        }
      }
      
      // Reset quicktale details to base state
      const baseDetails = {
        title: "",
        description: "",
        status: "public",
        approvalStatus: {
          status: "approved",
          updatedAt: currentTimestamp,
          updatedBy: currentUser?.id || "",
        },
        createdBy: actualUserId,
        createdByData: createdByData,
        narrator: actualUserId,
        narratorData: narratorData,
        organization: organizationId || currentUser?.organization || "",
        isDraft: true,
        createdAt: currentTimestamp,
        tags: [],
        location: "",
        locationDisplay: "",
        locationCountry: "",
        coordinates: new GeoPoint(0, 0),
        g: {
          geohash: "",
          geopoint: new GeoPoint(0, 0),
        },
        audioLength: 0,
        imageUrls: [],
        audioUrl: "",
        processedAudioUrl: "",
        originalAudioName: "",
        processedAudioName: "",
        originalPhotoName: "",
      };
      
      // Check for organization location and populate createdByData from the org.
      const orgId = organizationId || currentUser?.organization;
      if (orgId) {
        const orgDoc = await getSingleOrg(orgId);
        if (orgDoc && orgDoc.exists()) {
          const orgData = orgDoc.data();

          // Populate createdByData from the organization (createdBy is the org id).
          baseDetails.createdByData = {
            ...orgData,
            id: orgId,
            _sourceType: "organization",
          };

          if (hasValidOrgLocation(orgData)) {
            const locationData = await getLocationFromOrganization(orgData);
            
            if (locationData) {
              setQuicktaleDetails({
                ...baseDetails,
                ...locationData
              });
              setLoading(false);
              return;
            }
          }
        }
      }

      
      // No org location, check sessionStorage for user's preference
      const useCurrentLocation = sessionStorage.getItem('useCurrentLocation');
      
      setQuicktaleDetails(baseDetails);
      
      if (useCurrentLocation === 'true') {
        // User previously chose to use current location
        await handleUseCurrentLocation();
      } else if (useCurrentLocation === 'false') {
        // User previously declined current location
        await handleDeclineCurrentLocation();
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error resetting form:", error);
      Toast("error", "Error resetting form. Please try again.");
      setLoading(false);
    }
  };

  const handleCreateQuickTale = async () => {
    // Check required fields
    const hasImage = !!uploadImage;
    const hasAudio = !!quicktaleDetails.audioUrl;
    const hasTitle = !!quicktaleDetails.title?.trim();

    // Scenario A: Missing one or more required fields
    if (!hasImage || !hasAudio || !hasTitle) {
      const missing = [];
      if (!hasImage) missing.push("A Photo");
      if (!hasAudio) missing.push("An Audio Recording");
      if (!hasTitle) missing.push("A Title");
      
      setMissingFields(missing);
      setWarningModalType('missing');
      setWarningModalOpen(true);
      return; // Stop save process
    }

    // Scenario B: All fields present but user manually set isDraft to true
    if (quicktaleDetails.isDraft === true) {
      setWarningModalType('draft');
      setWarningModalOpen(true);
      return; // Stop save process
    }

    // If we get here, all fields present and isDraft is false
    // Proceed with normal save
    performSave();
  };

  const performSave = async () => {
    try {
      setLoading(true);

      // Check if narrator is a custom name (text) and not a user ID
      // If narratorData is null, it means they typed text or selected a custom narrator
      let customNarratorCreated = false;
      if (!quicktaleDetails.narratorData && quicktaleDetails.narrator) {
        const narratorName = quicktaleDetails.narrator.trim();
        
        // Get user ID to check/create custom narrators
        const userIdToUse = currentUser?.userId || currentUser?.uid;
        
        if (userIdToUse && narratorName) {
          try {
            // Fetch current custom narrators for this user
            const existingNarrators = await getCustomNarratorsByUser(userIdToUse);
            
            // Check if this narrator name already exists
            if (!narratorNameExists(narratorName, existingNarrators)) {
              // Create new custom narrator
              const newNarrator = await createCustomNarrator(narratorName, userIdToUse);
              if (newNarrator) {
                console.log("Created new custom narrator:", newNarrator);
                customNarratorCreated = true;
              }
            }
          } catch (error) {
            console.error("Error checking/creating custom narrator:", error);
            // Continue with save even if custom narrator creation fails
          }
        }
      }

      let title = quicktaleDetails.title || `Untitled ${moment().format("M-D-YYYY")}`;
      
      let dataTemp = {
        title: title,
        description: quicktaleDetails?.description || "",
        status: quicktaleDetails?.status || "public",
        approvalStatus: {
          status: quicktaleDetails?.approvalStatus?.status || "approved",
          updatedAt: Timestamp.now(),
          updatedBy: currentUser?.id || "",
        },
        createdBy: quicktaleDetails?.createdBy || currentUser?.id || "",
        narrator: quicktaleDetails?.narrator || currentUser?.id || "",
        organization: quicktaleDetails?.organization || organizationId || currentUser?.organization || "",
        isDraft: quicktaleDetails?.isDraft !== undefined ? quicktaleDetails.isDraft : true,
        location: quicktaleDetails?.location || "",
        locationDisplay: quicktaleDetails?.locationDisplay || "",
        locationCountry: quicktaleDetails?.locationCountry || "",
        coordinates: new GeoPoint(
          quicktaleDetails?.coordinates?._lat || 0,
          quicktaleDetails?.coordinates?._long || 0
        ),
        g: {
          geohash: quicktaleDetails?.g?.geohash || "",
          geopoint: new GeoPoint(
            quicktaleDetails?.coordinates?._lat || 0,
            quicktaleDetails?.coordinates?._long || 0
          ),
        },
        tags: Array.isArray(quicktaleDetails.tags) 
          ? quicktaleDetails.tags 
          : (typeof quicktaleDetails.tags === "string" 
            ? quicktaleDetails.tags.split(",") 
            : []),
        audioLength: quicktaleDetails?.audioLength || 0,
      };

      if (uploadImage != null) {
        let photoUrl = await uploadQuickTaleImageToStorage(uploadImage?.raw);
        dataTemp.imageUrls = [photoUrl];
        dataTemp.originalPhotoName = uploadImage?.raw?.name;
      }

      if (quicktaleDetails.audioUrl) {
        dataTemp.audioUrl = quicktaleDetails.audioUrl;
        dataTemp.originalAudioName = quicktaleDetails.originalAudioName;
      }

      if (quicktaleDetails.processedAudioUrl) {
        dataTemp.processedAudioUrl = quicktaleDetails.processedAudioUrl;
        dataTemp.processedAudioName = quicktaleDetails.processedAudioName;
      } else {
        dataTemp.processedAudioUrl = '';
        dataTemp.processedAudioName = '';
      }

      // Save userNoProcess field
      if (quicktaleDetails.userNoProcess !== undefined) {
        dataTemp.userNoProcess = quicktaleDetails.userNoProcess;
      }

      console.log("Creating QuickTale with data:", dataTemp);
      const newQuickTaleId = await createQuickTale(dataTemp);
      
      showNotification("QuickTale created successfully.");

      // Add QuickTale to selected areas
      const selectedAreaIds = Array.isArray(quicktaleDetails?.uiAreaIds)
        ? quicktaleDetails.uiAreaIds
        : [];

      if (selectedAreaIds.length > 0) {
        try {
          for (const areaId of selectedAreaIds) {
            const areaDocRef = doc(db, "area", areaId);
            const areaSnap = await getDoc(areaDocRef);
            if (areaSnap.exists()) {
              const areaData = areaSnap.data();
              const updatedAlbumsAndQTs = upsertQuickTaleInAlbumsAndQTs({
                albumsAndQTs: areaData?.albumsAndQTs || [],
                qtId: newQuickTaleId,
              });
              await updateDoc(areaDocRef, { albumsAndQTs: updatedAlbumsAndQTs });
            }
          }
          console.log("QuickTale added to areas:", selectedAreaIds);
        } catch (areaError) {
          console.error("Error adding QuickTale to areas:", areaError);
          // Non-blocking — continue with save
        }
      }

      // Update AddSearch index after successful creation
      try {
        const functions = getFunctions();
        const updateQTInAddSearch = httpsCallable(functions, 'updateQTInAddSearch');
        
        await updateQTInAddSearch({
          docId: newQuickTaleId,
          data: {
            ...dataTemp,
            createdAt: Timestamp.now(),
            creator: dataTemp.createdBy,
            environment: process.env.REACT_APP_PROJECT_ENVIROMENT,
          },
          docType: "quicktale",
        });
        
        console.log("AddSearch index updated for new QuickTale");
      } catch (error) {
        console.error("Error updating QuickTale in AddSearch:", error);
      }
      
      // Index the new QuickTale's image for image search.
      if (uploadImage != null && dataTemp.imageUrls?.[0]) {
        // (a) Google Vertex AI vision embeddings — the new image-search system.
        try {
          console.log("Indexing new QuickTale image to Vision");

          const functions = getFunctions();
          const indexSingleQuickTaleToVision = httpsCallable(functions, 'indexSingleQuickTaleToVision');

          const visionResult = await indexSingleQuickTaleToVision({
            quicktaleId: newQuickTaleId,
            imageUrl: dataTemp.imageUrls[0],
          });

          console.log("Vision index result for new QuickTale:", visionResult.data);
        } catch (error) {
          console.error("Error indexing new QuickTale to Vision:", error);
        }

        // (b) Clarifai — legacy image-search system, kept during the transition.
        //     Clarifai is being retired; this call must fail silently so its
        //     eventual shutdown never blocks a QuickTale save.
        try {
          console.log("Updating Clarifai with image for new QuickTale");

          const functions = getFunctions();
          const updateClarifaiInput = httpsCallable(functions, 'updateClarifaiInput');

          await updateClarifaiInput({
            quicktaleId: newQuickTaleId,
            imageUrl: dataTemp.imageUrls[0],
            metadata: {
              ORG_ID: dataTemp.organization || "",
              QTID: newQuickTaleId,
              description: dataTemp.description || "",
              location: dataTemp.location || "",
              locationDisplay: dataTemp.locationDisplay || "",
              tags: dataTemp.tags || [],
              title: title
            }
          });
          
          console.log("Clarifai update successful for new QuickTale");
        } catch (error) {
          console.error("Error updating QuickTale in Clarifai:", error);
        }
      }

      // Fire-and-forget: process audio in background via V5
      if (dataTemp.audioUrl && !dataTemp.processedAudioUrl) {
        try {
          const auth = getAuth();
          const idToken = await auth.currentUser.getIdToken();
          const projectId = auth.app.options.projectId;
          const functionUrl = `https://us-central1-${projectId}.cloudfunctions.net/processAudioWithAuphonicV5`;

          console.log("Fire-and-forget audio processing via V5 for QuickTale:", newQuickTaleId);
          fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              fileUrl: dataTemp.audioUrl,
              qtTitle: dataTemp.title || '',
              ogFileName: dataTemp.originalAudioName || 'audio',
              collectionName: 'quicktales',
              documentId: newQuickTaleId,
            }),
          }).catch(err => console.error('Fire-and-forget audio processing error:', err));
        } catch (audioProcessErr) {
          console.error("Error initiating fire-and-forget audio processing:", audioProcessErr);
          // Non-blocking — don't show error to user
        }
      }

      // Show success modal instead of navigating
      setLoading(false);
      setQuicktaleSavedModalOpen(true);
    } catch (error) {
      Toast("error", error.message);
      console.log("error creating quicktale", error.message);
    } finally {
      setLoading(false);
    }
  };

  // Show layout with sidebar and loading state until quicktaleDetails is initialized
  if (!quicktaleDetails.createdAt) {
    return (
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <DetailsPageHeader
          mainTitle="Create QuickTale"
          bgColor="#fff"
          buttonText="Save"
          editEnabled={false}
          setEditEnabled={() => {}}
          cancelButton={false}
          onCancelClick={() => {}}
          handleSave={() => {}}
          loading={true}
          hideBackArrow={true}
        />
        <Sidebar />
        <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
          <Grid container direction={"column"} style={subContainerStyles}>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
              <span style={{ color: "#999", fontSize: 16 }}>Loading...</span>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle="Create QuickTale"
        bgColor="#fff"
        buttonText="Save"
        editEnabled={true}
        setEditEnabled={() => {}}
        cancelButton={true}
        onCancelClick={handleCancelClick}
        handleSave={handleCreateQuickTale}
        loading={loading}
        hideBackArrow={true}
      />
      <Sidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} style={subContainerStyles}>
          {/*Data card/container section*/}
          <CreateQTDataFieldsSection
            quicktaleDetails={quicktaleDetails}
            setQuicktaleDetails={setQuicktaleDetails}
            handleImagePreviewModalOpen={handleImagePreviewModalOpen}
            handleAudioModalOpen={handleAudioModalOpen}
            editEnabled={true}
            uploadImage={uploadImage}
            setUploadImage={setUploadImage}
            uploadAudioFile={uploadAudioFile}
            setUploadAudioFile={setUploadAudioFile}
            handleFileUpload={handleFileUpload}
          />
          {/*interactions card/container section
          <CreateQTDetailsInteractionsFieldsSection
            quicktaleDetails={quicktaleDetails}
            setQuicktaleDetails={setQuicktaleDetails}
          />*/}
          {/*organization card/container section - HIDDEN FOR ORGANIZATION USERS*/}
          {/*QTDetailsOrganizationSection conditionally hidden for organization users per requirements*/}
        </Grid>
      </Grid>
      <ImagePreviewModal
        modalOpen={imagePreviewModal}
        handleClose={handleImagePreviewModalClose}
        image={
          uploadImage != null && uploadImage.preview
            ? uploadImage.preview
            : quicktaleDetails.photoUrl
        }
      />
      <AudioModal
        modalOpen={audioModal}
        handleClose={handleAudioModalClose}
        audioFile={
          uploadAudioFile != null && uploadAudioFile.preview
            ? uploadAudioFile.preview
            : quicktaleDetails.audioUrl
        }
      />
      <AudioModal
        modalOpen={processedAudioModal}
        handleClose={handleProcessedAudioModalClose}
        audioFile={
          uploadProcessedAudioFile != null && uploadProcessedAudioFile.preview
            ? uploadProcessedAudioFile.preview
            : quicktaleDetails.processedAudioUrl
        }
      />
      <ConfirmationModal
        open={confirmationModalOpen}
        onClose={handleConfirmationModalClose}
        onConfirm={handleProcessAudio}
        title="Process Audio"
        message="Would you like to clean and normalize audio?"
      />
      <ConfirmationModal
        open={cancelConfirmationModalOpen}
        onClose={() => setCancelConfirmationModalOpen(false)}
        onConfirm={handleConfirmCancel}
        title="Cancel Creation"
        message="Are you sure you want to cancel? All data will be lost."
      />
      <ConfirmationModal
        open={locationPermissionModalOpen}
        onClose={handleDeclineCurrentLocation}
        onConfirm={handleUseCurrentLocation}
        title="Use Current Location?"
        message="Would you like to use your current location for this QuickTale? If you decline, we'll use your approximate location based on your IP address."
      />
      <QuickTaleSavedModal
        open={quicktaleSavedModalOpen}
        onReturn={handleReturnToList}
        onCreate={handleCreateAnother}
      />
      <QuickTaleWarningModal
        open={warningModalOpen}
        onClose={handleWarningModalClose}
        onSaveAsDraft={handleWarningModalSaveAsDraft}
        onFinishQuickTale={handleWarningModalFinishQuickTale}
        type={warningModalType}
        missingFields={missingFields}
      />
    </Box>
  );
};

export default CreateQuickTaleContainer;

const subContainerStyles = {
  paddingLeft: 30,
  paddingRight: 30,
  marginTop: 50,
};
